import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import {
  createWorklog,
  deleteWorklog,
  getWorklogs,
  updateWorklog,
} from "../api/worklogApi";
import {
  canEditWorklogEntry,
  createLocalWorklog,
  formatLoggedDuration,
  getWorklogDateRestrictionMessage,
  normalizeWorklogFromApi,
} from "../utils/worklogUtils";
import LogWorkTimeModal from "./LogWorkTimeModal";

/* ── Sample WorkLog data (fallback until APIs are live) ─────────────────── */

const SAMPLE_WORKLOGS = [
  {
    id: 1,
    userName: "Arjun Sharma",
    initials: "AS",
    minutes: 120,
    loggedAt: "14 May 2026 · 10:19",
    _isSample: true,
  },
];

const mapWorklogs = (items, fallbackUser) =>
  (items || [])
    .map((item) => normalizeWorklogFromApi(item, fallbackUser))
    .filter(Boolean);

const withoutSampleLogs = (items) => items.filter((item) => !item._isSample);

const WorkLogFooterBar = ({ onAddLog }) => (
  <div className="wl-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <span className="wl-footer-hint" style={{ lineHeight: 1 }}>Don't forget to log today's hours</span>
      <span style={{ fontSize: "0.72rem", color: "#ef4444", lineHeight: 1, fontWeight: 500 }}>
        * {getWorklogDateRestrictionMessage()}
      </span>
    </div>
    <button className="wl-log-btn wl-log-btn--outline" onClick={onAddLog}>
      + Log Time
    </button>
  </div>
);

const WorkLogSummaryBar = ({ totalLogged, est, remaining, overBudget, pct }) => (
  <div className="wl-summary-card">
    <div className="wl-summary">
      <div className="wl-summary-cell">
        <span className="wl-summary-label">TOTAL LOGGED</span>
        <span className="wl-summary-value">{formatLoggedDuration(totalLogged)}</span>
      </div>
      <div className="wl-summary-cell wl-summary-cell--center">
        <span className="wl-summary-label">ESTIMATED</span>
        <span className="wl-summary-value">{est ? formatLoggedDuration(est) : "—"}</span>
      </div>
      <div className="wl-summary-cell wl-summary-cell--right">
        <span className="wl-summary-label">REMAINING</span>
        <span className={`wl-summary-value${overBudget ? " wl-over" : ""}`}>
          {remaining === null ? "—" : overBudget ? "Over" : formatLoggedDuration(remaining)}
        </span>
      </div>
    </div>
    <div className="wl-progress-track">
      <div
        className={`wl-progress-bar${overBudget ? " wl-progress-bar--over" : ""}`}
        style={{ width: est > 0 ? `${pct}%` : totalLogged > 0 ? "100%" : "0%" }}
      />
    </div>
  </div>
);

/* ── WorkLogPanel ───────────────────────────────────────────────────────── */

const WorkLogPanel = React.memo(({
  taskId,
  estimatedMinutes,
  loggedMinutes: loggedMinutesFromTask,
  taskCode,
  onWorklogChange,
  openLogModalSignal = 0,
}) => {
  const authUser = useSelector((state) => state.auth.user) ?? {};
  const lastProcessedLogSignalRef = useRef(0);
  const prevTaskIdRef = useRef(undefined);
  const fallbackUser = useMemo(
    () => ({
      name: authUser.name || authUser.fullName || authUser.userName,
      email: authUser.email,
    }),
    [authUser.email, authUser.fullName, authUser.name, authUser.userName]
  );

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(Boolean(taskId));
  const [usingFallback, setUsingFallback] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [logToEdit, setLogToEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadWorklogs = useCallback(async () => {
    if (!taskId) {
      setLogs(SAMPLE_WORKLOGS);
      setUsingFallback(true);
      return;
    }

    setLoading(true);
    try {
      const items = await getWorklogs(taskId);
      const normalized = mapWorklogs(items, fallbackUser);
      setLogs(normalized.length > 0 ? normalized : []);
      setUsingFallback(false);
    } catch (err) {
      console.warn("[WorkLogPanel] Falling back to sample worklogs:", err?.message);
      setLogs(SAMPLE_WORKLOGS);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, [taskId, fallbackUser]);

  useEffect(() => {
    loadWorklogs();
  }, [loadWorklogs]);

  useEffect(() => {
    if (openLogModalSignal <= lastProcessedLogSignalRef.current) return;
    lastProcessedLogSignalRef.current = openLogModalSignal;
    setLogToEdit(null);
    setIsLogModalOpen(true);
  }, [openLogModalSignal]);

  useEffect(() => {
    if (prevTaskIdRef.current === undefined) {
      prevTaskIdRef.current = taskId;
      return;
    }
    if (prevTaskIdRef.current === taskId) return;
    prevTaskIdRef.current = taskId;
    lastProcessedLogSignalRef.current = 0;
    setIsLogModalOpen(false);
    setLogToEdit(null);
  }, [taskId]);

  const worklogSum = logs.reduce((sum, log) => sum + (log.minutes || 0), 0);
  const totalLogged = !usingFallback
    ? worklogSum
    : loggedMinutesFromTask ?? worklogSum;
  const est = estimatedMinutes || 0;
  const remaining = est > 0 ? est - totalLogged : null;
  const overBudget = remaining !== null && remaining <= 0;
  const pct = est > 0 ? Math.min(100, Math.round((totalLogged / est) * 100)) : 0;

  const resolvedTaskCode = taskCode || `TSK-${taskId || "0001"}-002`;

  const closeModal = () => {
    setIsLogModalOpen(false);
    setLogToEdit(null);
  };

  const handleEditLog = (log) => {
    setLogToEdit(log);
    setIsLogModalOpen(true);
  };

  const handleAddLog = () => {
    setLogToEdit(null);
    setIsLogModalOpen(true);
  };

  const upsertLocalLog = (payload, existingLog = null) => {
    const nextLog = createLocalWorklog(
      {
        ...payload,
        id: existingLog?.id,
      },
      fallbackUser
    );

    setLogs((prev) => {
      if (existingLog?.id) {
        return prev.map((item) => (item.id === existingLog.id ? nextLog : item));
      }
      return [nextLog, ...prev];
    });
  };

  const handleSaveLog = async (payload, existingLog = null) => {
    if (!taskId) {
      upsertLocalLog(payload, existingLog);
      toast.success(existingLog ? "Work log updated locally." : "Work log saved locally.");
      closeModal();
      return;
    }

    setSaving(true);
    try {
      const apiPayload = {
        date: payload.date,
        timeSpent: payload.timeSpent,
        description: payload.description,
      };

      const saved = existingLog?.id
        ? await updateWorklog(taskId, existingLog.id, apiPayload)
        : await createWorklog(taskId, apiPayload);

      const normalized = normalizeWorklogFromApi(
        {
          ...saved,
          timeSpentLabel: payload.timeSpentLabel,
        },
        fallbackUser
      );

      if (!normalized) {
        throw new Error("Work log saved but response was invalid.");
      }

      setLogs((prev) => {
        const base = withoutSampleLogs(prev);
        if (existingLog?.id) {
          return base.map((item) => (item.id === existingLog.id ? normalized : item));
        }
        return [normalized, ...base];
      });
      setUsingFallback(false);
      toast.success(existingLog ? "Work log updated." : "Work log saved.");
      onWorklogChange?.();
      closeModal();
    } catch (err) {
      if (usingFallback) {
        upsertLocalLog(payload, existingLog);
        toast.warn(
          existingLog
            ? "API unavailable. Work log updated locally."
            : "API unavailable. Work log saved locally."
        );
        closeModal();
        return;
      }

      toast.error(err?.message || "Failed to save work log.");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLog = async (log) => {
    if (!window.confirm("Delete this work log entry?")) return;

    if (!taskId) {
      setLogs((prev) => prev.filter((item) => item.id !== log.id));
      toast.success("Work log deleted locally.");
      return;
    }

    setDeletingId(log.id);
    try {
      await deleteWorklog(taskId, log.id);
      setLogs((prev) => withoutSampleLogs(prev).filter((item) => item.id !== log.id));
      setUsingFallback(false);
      toast.success("Work log deleted.");
      onWorklogChange?.();
    } catch (err) {
      if (usingFallback) {
        setLogs((prev) => prev.filter((item) => item.id !== log.id));
        toast.warn("API unavailable. Work log deleted locally.");
        return;
      }
      toast.error(err?.message || "Failed to delete work log.");
    } finally {
      setDeletingId(null);
    }
  };

  const isEmpty = !loading && (!logs || logs.length === 0);

  const modalProps = {
    open: isLogModalOpen,
    onClose: closeModal,
    taskCode: resolvedTaskCode,
    estimatedMinutes: est,
    totalLoggedMinutes: totalLogged,
    initialLog: logToEdit,
    saving,
    onSave: handleSaveLog,
  };

  const summaryProps = { totalLogged, est, remaining, overBudget, pct };

  if (loading) {
    return (
      <div className="wl-root">
        <WorkLogSummaryBar {...summaryProps} />
        <div className="wl-loading">Loading work logs...</div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <React.Fragment>
        <div className="wl-root">
          <WorkLogSummaryBar {...summaryProps} />
          <div className="wl-empty">
          <div
            className="wl-empty-icon"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "56px",
              height: "56px",
              background: "#fff1f2",
              border: "1.5px solid #fecaca",
              borderRadius: "50%",
              margin: "0 auto 12px",
            }}
          >
            <span style={{ fontSize: "28px", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.05))" }}>
              ⏱️
            </span>
          </div>
          <p className="wl-empty-title">No time logged yet</p>
          <p className="wl-empty-sub">
            Track your effort by logging the time you spend on this task. It helps with estimation
            accuracy and team reporting.
          </p>
          <button className="wl-log-btn" onClick={handleAddLog}>
            + Log Time
          </button>
          </div>
        </div>
        {isLogModalOpen && <LogWorkTimeModal {...modalProps} />}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="wl-root">
        <WorkLogSummaryBar {...summaryProps} />
        <WorkLogFooterBar onAddLog={handleAddLog} />

        <div className="wl-entries">
          {logs.map((log) => {
            const canEdit = canEditWorklogEntry(log);
            return (
            <div key={log.id} className="wl-entry">
              <div className="wl-entry-left">
                <span className="wl-avatar">{log.initials}</span>
                <div className="wl-entry-info">
                  <div className="wl-entry-name">
                    {log.userName}
                    <span className="wl-entry-duration">⏱️{formatLoggedDuration(log.minutes)}</span>
                  </div>
                  <span className="wl-entry-time">{log.loggedAt}</span>
                  {log.description ? (
                    <span className="wl-entry-description">{log.description}</span>
                  ) : null}
                </div>
              </div>
              <div className="wl-entry-actions">
                <button
                  className="wl-action-btn"
                  title={canEdit ? "Edit" : getWorklogDateRestrictionMessage()}
                  onClick={() => handleEditLog(log)}
                  disabled={!canEdit || deletingId === log.id}
                >
                  <LuPencil size={14} />
                </button>
                <button
                  className="wl-action-btn wl-action-btn--delete"
                  title="Delete"
                  onClick={() => handleDeleteLog(log)}
                  disabled={deletingId === log.id}
                >
                  <LuTrash2 size={14} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>
      {isLogModalOpen && <LogWorkTimeModal {...modalProps} />}
    </React.Fragment>
  );
});

WorkLogPanel.displayName = "WorkLogPanel";

export default WorkLogPanel;
